import React from 'react'
import { useSelector } from 'react-redux'

import { Wrap, Title, ListWrap, RowWrap, SubTitle } from './styled'
import { getSameAnswers, getDisagreedQuestions } from './utils'
import { SameAnswersList } from './SameAnswersList'
import { DisagreeAnswersList, DisagreedQuestion } from './DisagreeAnswersList'

import { QuizAnswers } from 'src/api/quizAnswers/types'
import { PhotoProgressBar } from 'src/components/PhotoProgressBar/PhotoProgressBar'
import { ActionAnswer } from 'src/api/quizzes/types'
import { iRootState } from 'src/store'
import { useMedia } from 'src/hooks/useMedia'
import { breakpoints } from 'src/styles/media'
import { CompletedQuiz } from 'src/api/connections/types'
import { QuizIcon } from 'src/components/QuizIcon/QuizIcon'

enum Subtitle {
  Love = 'You Both Love...',
  Like = 'You Both Like...',
  Dunno = 'You Both Dunno...',
  DontLike = `You Both Don't Like...`,
  Skip = 'You Both Skipped',
  Disagree = 'You Disagree...',
  Default = 'No common answers yet',
}

export const getSectionTitle = (
  firstSubtitle: Subtitle,
  possibleSubtitle: Subtitle
) => {
  if (firstSubtitle === possibleSubtitle) {
    return undefined
  }
  return possibleSubtitle
}

const getFirstTitle = ({
  youBothLove,
  youBothLike,
  youBothDunno,
  youBothDontLike,
  youDisagree,
}: {
  youBothLove: string[]
  youBothLike: string[]
  youBothDunno: string[]
  youBothDontLike: string[]
  youDisagree: DisagreedQuestion[]
}) => {
  if (youBothLove.length > 0) return Subtitle.Love
  if (youBothLike.length > 0) return Subtitle.Like
  if (youBothDunno.length > 0) return Subtitle.Dunno
  if (youBothDontLike.length > 0) return Subtitle.DontLike
  if (youDisagree.length > 0) return Subtitle.Disagree
  return Subtitle.Default
}

const stateSelector = (state: iRootState) => ({
  profilePicture: state.user.data?.profilePicture,
})

interface Props {
  quizAnswers: QuizAnswers
  otherUserProfilePhoto: string
  matchPercent: number
  selectedQuiz?: CompletedQuiz
}

export const OneQuizComparison: React.FC<Props> = ({
  quizAnswers,
  otherUserProfilePhoto,
  matchPercent,
  selectedQuiz,
}) => {
  const isDesktop = useMedia(breakpoints.desktop)

  const { profilePicture } = useSelector(stateSelector)

  const sameAnswers = React.useMemo(
    () => ({
      youBothLove: getSameAnswers(quizAnswers, ActionAnswer.up),
      youBothLike: getSameAnswers(quizAnswers, ActionAnswer.right),
      youBothDunno: getSameAnswers(quizAnswers, ActionAnswer.left),
      youBothDontLike: getSameAnswers(quizAnswers, ActionAnswer.down),
    }),
    [quizAnswers]
  )

  const disagreedQuestions = React.useMemo(
    () => getDisagreedQuestions(quizAnswers),
    [quizAnswers]
  )

  const firstSubtitle = React.useMemo(
    () => getFirstTitle({ ...sameAnswers, youDisagree: disagreedQuestions }),
    [disagreedQuestions, sameAnswers]
  )

  return (
    <Wrap>
      {!isDesktop && selectedQuiz && (
        <QuizIcon
          imgSrc={selectedQuiz.imageUrl}
          color={selectedQuiz.colors.bgStart}
        />
      )}
      <Title>{selectedQuiz?.title}</Title>
      <ListWrap>
        <RowWrap>
          <PhotoProgressBar
            size={40}
            strokeWidth={2}
            imgUrl={otherUserProfilePhoto}
            uniqueIdentifier={1}
            progress={matchPercent}
          />
          <SubTitle>{firstSubtitle}</SubTitle>
          {profilePicture && (
            <PhotoProgressBar
              size={40}
              strokeWidth={2}
              imgUrl={profilePicture}
              uniqueIdentifier={2}
              progress={0}
            />
          )}
        </RowWrap>
        <SameAnswersList
          questionTitles={sameAnswers.youBothLove}
          answerEmotion={ActionAnswer.up}
          subtitle={getSectionTitle(firstSubtitle, Subtitle.Love)}
        />
        <SameAnswersList
          questionTitles={sameAnswers.youBothLike}
          answerEmotion={ActionAnswer.right}
          subtitle={getSectionTitle(firstSubtitle, Subtitle.Like)}
        />
        <SameAnswersList
          questionTitles={sameAnswers.youBothDunno}
          answerEmotion={ActionAnswer.left}
          subtitle={getSectionTitle(firstSubtitle, Subtitle.Dunno)}
        />
        <SameAnswersList
          questionTitles={sameAnswers.youBothDontLike}
          answerEmotion={ActionAnswer.down}
          subtitle={getSectionTitle(firstSubtitle, Subtitle.DontLike)}
        />
        <DisagreeAnswersList
          subtitle={getSectionTitle(firstSubtitle, Subtitle.Disagree)}
          disagreedQuestions={disagreedQuestions}
        />
      </ListWrap>
    </Wrap>
  )
}
